<script setup lang="ts">
  import { computed, useId } from 'vue'
  import BfIconCheck from '@/components/ui/icons/BfIconCheck.vue'
  import type { SuccessScreenButton, SuccessScreenInfo } from '@/components/ui/bahnhof/BfSuccessScreenTypes'

  const props = withDefaults(
    defineProps<{
      title: string
      description?: string | string[]
      srMessage?: string
      info?: SuccessScreenInfo
      buttons?: SuccessScreenButton[]
    }>(),
    {
      description: undefined,
      srMessage: undefined,
      info: undefined,
      buttons: () => [],
    },
  )

  const titleId = useId()

  const paragraphs = computed<string[]>(() => {
    if (!props.description) return []
    const lines = Array.isArray(props.description) ? props.description : props.description.split('\n')
    return lines.map(line => line.trim()).filter(Boolean)
  })
</script>

<template>
  <div class="success-screen" :aria-labelledby="titleId">
    <div class="success-screen__mark" aria-hidden="true">
      <span class="success-screen__mark-ring" />
      <span class="success-screen__mark-disc">
        <BfIconCheck :size="22" />
      </span>
    </div>

    <h1 :id="titleId" class="success-screen__title">
      {{ title }}
      <span v-if="srMessage" class="sr-only">{{ srMessage }}</span>
    </h1>

    <p v-for="(paragraph, index) in paragraphs" :key="index" class="success-screen__text" aria-live="polite">
      {{ paragraph }}
    </p>

    <div v-if="info" class="success-screen__info">
      <span class="success-screen__info-label">{{ info.label }}</span>
      <span class="success-screen__info-value" :aria-label="info.valueAriaLabel">{{ info.value }}</span>
    </div>

    <div v-if="$slots.default" class="success-screen__content">
      <slot />
    </div>

    <nav v-if="buttons.length" class="success-screen__actions" aria-label="Next steps">
      <BfButton
        v-for="button in buttons"
        :key="button.label"
        :variant="button.variant || 'ghost'"
        :sweep="(button.variant || 'ghost') === 'primary'"
        :aria-describedby="button.describedBy"
        @click="button.onClick?.()"
      >
        {{ button.label }}
      </BfButton>
    </nav>

    <slot name="footer" />
  </div>
</template>

<style scoped lang="scss">
  .success-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 580px;
    margin: 0 auto;
    padding: 40px 24px 48px;
    text-align: center;
  }

  .success-screen__mark {
    position: relative;
    width: 44px;
    height: 44px;
    margin-bottom: 28px;
  }

  .success-screen__mark-ring {
    position: absolute;
    inset: 0;
    border-radius: var(--bf-r-pill);
    background: rgba(1, 179, 87, 0.18);
    animation: success-screen-ring 1.4s var(--bf-ease) 0.15s forwards;
  }

  .success-screen__mark-disc {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: var(--bf-r-pill);
    background: var(--bf-green);
    color: var(--bf-white);
    animation: success-screen-pop 0.32s var(--bf-ease) both;
  }

  .success-screen__title {
    margin: 0 0 16px;
    font-family: var(--bf-font-display);
    font-size: 40px;
    font-weight: var(--bf-fw-bold);
    line-height: 1.05;
    letter-spacing: -0.025em;
    color: var(--bf-deep-blue);
    font-variant-numeric: tabular-nums;
    animation: success-screen-fade-up 0.35s var(--bf-ease) 0.1s both;
  }

  .success-screen__text {
    max-width: 480px;
    margin: 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-base);
    line-height: 1.6;
    color: var(--bf-dusky-1);
    animation: success-screen-fade-up 0.35s var(--bf-ease) 0.18s both;

    & + & {
      margin-top: 12px;
    }
  }

  .success-screen__content {
    width: 100%;
    margin-top: 28px;
    text-align: left;
  }

  .success-screen__info {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: var(--bf-sp-2);
    margin-top: 28px;
    padding: 12px 18px;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-sm);
    background: var(--bf-blue-light);
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-md);
    animation: success-screen-fade-up 0.35s var(--bf-ease) 0.24s both;
  }

  .success-screen__info-label {
    color: var(--bf-fg-2);
  }

  .success-screen__info-value {
    font-family: var(--bf-font-display);
    font-weight: var(--bf-fw-bold);
    font-variant-numeric: tabular-nums;
    color: var(--bf-deep-blue);
  }

  .success-screen__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 36px;
  }

  @keyframes success-screen-pop {
    from {
      transform: scale(0.6);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes success-screen-ring {
    from {
      transform: scale(0.85);
      opacity: 0.45;
    }
    to {
      transform: scale(1.35);
      opacity: 0;
    }
  }

  @keyframes success-screen-fade-up {
    from {
      transform: translateY(6px);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }

  @media (max-width: 560px) {
    .success-screen {
      padding: 32px 16px 40px;
    }

    .success-screen__title {
      font-size: 28px;
    }

    /* Stacked, but still in the order the buttons were given: the way back stays first,
       so the column reads the same left-to-right row it replaces. */
    .success-screen__actions {
      flex-direction: column;
      align-self: stretch;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .success-screen__mark-ring,
    .success-screen__mark-disc,
    .success-screen__title,
    .success-screen__text,
    .success-screen__info {
      animation: none;
    }
  }
</style>
